// This site is a pure client-side SPA — no server runtime. `submitStory`
// used to be a `createServerFn` back when SSR existed; in the current build
// its handler would execute in the browser, where `process.env` is empty
// and the service-role Supabase client cannot be constructed. Instead we
// call the already-deployed Edge Function `submit-story`, which performs
// honeypot / time-on-form / challenge / rate-limit checks and the two
// inserts (stories + submissions_raw) with the service role.
//
// Kept as a plain async function with the same `{ data }` call signature
// so the wizard call site does not change.

import { supabase } from "@/integrations/supabase/client";
import { SubmitPayloadSchema, type SubmitPayload } from "./submit-shared";

export async function submitStory({ data }: { data: unknown }): Promise<{ ok: true }> {
  const payload: SubmitPayload = SubmitPayloadSchema.parse(data);

  const { data: result, error } = await supabase.functions.invoke("submit-story", {
    body: payload,
  });

  if (error) {
    // supabase-js's FunctionsHttpError puts the raw Response on `error.context`
    // and only exposes a generic "Edge Function returned a non-2xx status code"
    // in `error.message`. The Edge Function replies with `{ error: "<msg>" }`;
    // surface that exact wording to the author.
    const ctx = (error as { context?: unknown }).context;
    if (ctx && typeof (ctx as Response).json === "function") {
      try {
        const body = (await (ctx as Response).json()) as { error?: string };
        if (body?.error) throw new Error(body.error);
      } catch (e) {
        if (e instanceof Error && e.message && e.message !== "Edge Function returned a non-2xx status code") {
          throw e;
        }
      }
    }
    throw new Error(error.message || "Could not save submission. Please try again.");
  }

  return (result as { ok: true }) ?? { ok: true };
}